import React from "react";
import {
  Authenticator,
  AmplifyTheme,
  AmplifyMessageMap,
  Greetings,
  SignIn,
  ConfirmSignIn,
  RequireNewPassword,
  SignUp,
  Loading,
  ConfirmSignUp,
  VerifyContact,
  ForgotPassword,
  TOTPSetup
} from "aws-amplify-react";
import { I18n, Logger, Auth } from "aws-amplify";
import * as Constants from "aws-amplify-react/dist/Auth/common/constants";
import { Container, Toast } from "aws-amplify-react/dist/Amplify-UI/Amplify-UI-Components-React";
import { auth } from "aws-amplify-react/dist/Amplify-UI/data-test-attributes";

const AUTHENTICATOR_AUTHSTATE = "amplify-authenticator-authState";
const logger = new Logger("CustomAuthenticator", "DEBUG");
export const EmptyContainer = ({ children }) => <>{children}</>;

export default class CustomAuthenticator extends Authenticator {
  componentDidMount() {
    this._isMounted = true;
    const byHostedUI = localStorage.getItem(Constants.SIGNING_IN_WITH_HOSTEDUI_KEY);
    localStorage.removeItem(Constants.SIGNING_IN_WITH_HOSTEDUI_KEY);
    if (byHostedUI !== "true") this.checkUser();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleStateChange(state, data) {
    if (state === "signedOut") {
      state = "signIn";
    }

    if (this._isMounted) {
      this.setState({ authState: state, authData: data, error: null, showToast: false });
    }

    if (state === "signIn" && this.props.isAuthorized === true) {
      this.props.setShowToast(false);
    }

    if (state === "signedIn") {
      this.props.setShowToast(false);
      let isAuthenticated = false;
      if (
        data &&
        data.signInUserSession &&
        data.signInUserSession.idToken &&
        data.signInUserSession.idToken.payload &&
        data.signInUserSession.idToken.payload["cognito:groups"] &&
        data.signInUserSession.idToken.payload["cognito:groups"].length
      ) {
        isAuthenticated = true;
      }

      if (!isAuthenticated) {
        logger.error("Unauthenticated user");
        Auth.signOut()
          .then(() => {
            state = "signedOut";
            if (this.props.onStateChange) {
              this.props.onStateChange(state, data);
            }
            if (this._isMounted) {
              this.setState({ error: "Unauthenticated user", showToast: true });
            }
          })
          .catch(err => {
            if (this.props.onStateChange) {
              this.props.onStateChange(state, data);
            }
            logger.error(err);
          });
      } else {
        if (this.props.onStateChange) {
          this.props.onStateChange(state, data);
        }
        // TODO getUserPrivileges

        this.props.getUserPrivileges(this.props.appId, this.props.hotelId);
      }
    } else if (this.props.onStateChange) {
      this.props.onStateChange(state, data);
    }
  }

  checkUser() {
    if (!Auth || typeof Auth.currentAuthenticatedUser !== "function") {
      throw new Error("No Auth module found, please ensure @aws-amplify/auth is imported");
    }
    return Auth.currentAuthenticatedUser({ bypassCache: true })
      .then(user => {
        if (!this._isMounted) {
          return;
        }
        this.handleStateChange("signedIn", user);
      })
      .catch(err => {
        if (!this._isMounted) {
          return;
        }
        let cachedAuthState = null;
        try {
          cachedAuthState = localStorage.getItem(AUTHENTICATOR_AUTHSTATE);
        } catch (e) {
          logger.error("Failed to get the auth state from local storage", e);
        }
        const promise = cachedAuthState === "signedIn" ? Auth.signOut() : Promise.resolve();
        promise
          .then(() => this.handleStateChange(this._initialAuthState))
          .catch(e => {
            logger.error("Failed to sign out", e);
          });
      });
  }

  render() {
    const { authState, authData } = this.state;
    const theme = this.props.theme || AmplifyTheme;
    const messageMap = this.props.errorMessage || AmplifyMessageMap;
    // If container prop is undefined, default to AWS Amplify UI Container
    // otherwise if truthy, use the supplied render prop
    // otherwise if falsey, use EmptyContainer
    const Wrapper = this.props.container === undefined ? Container : this.props.container || EmptyContainer;
    let { hideDefault, hide = [], federated, signUpConfig, usernameAttributes, isUserPrivilegesLoading } = this.props;

    if (hideDefault) {
      hide = hide.concat([
        Greetings,
        SignIn,
        ConfirmSignIn,
        RequireNewPassword,
        SignUp,
        ConfirmSignUp,
        VerifyContact,
        ForgotPassword,
        TOTPSetup,
        Loading
      ]);
    }
    let props_children = [];
    if (typeof this.props.children === "object") {
      if (Array.isArray(this.props.children)) {
        props_children = this.props.children;
      } else {
        props_children.push(this.props.children);
      }
    }
    const default_children = [
      <Greetings federated={federated} />,
      <SignIn federated={federated} />,
      <ConfirmSignIn />,
      <RequireNewPassword />,
      <SignUp signUpConfig={signUpConfig} />,
      <ConfirmSignUp />,
      <VerifyContact />,
      <ForgotPassword />,
      <TOTPSetup />,
      <Loading />
    ];
    const props_children_override = React.Children.map(props_children, child => child.props.override);
    hide = hide.filter(component => !props_children.find(child => child.type === component));

    const render_props_children = React.Children.map(props_children, (child, index) => {
      return React.cloneElement(child, {
        key: `aws-amplify-authenticator-props-children-${index}`,
        theme,
        messageMap,
        authState,
        authData,
        onStateChange: this.handleStateChange,
        onAuthEvent: this.handleAuthEvent,
        hide,
        override: props_children_override,
        usernameAttributes
      });
    });

    const render_default_children = hideDefault
      ? []
      : React.Children.map(default_children, (child, index) => {
        return React.cloneElement(child, {
          key: `aws-amplify-authenticator-default-children-${index}`,
          theme,
          messageMap,
          authState,
          authData,
          onStateChange: this.handleStateChange,
          onAuthEvent: this.handleAuthEvent,
          hide,
          override: props_children_override,
          usernameAttributes
        });
      });
    const render_children = render_default_children.concat(render_props_children);
    const { error } = this.state;
    const fullScreenStyle = {
      position: "fixed",
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      overflow: "auto"
      // background: '#000000'
    };
    return (
      <Wrapper theme={theme} style={fullScreenStyle}>
        {this.state.showToast && (
          <Toast theme={theme} onClose={() => this.setState({ showToast: false })} data-test={auth.signIn.signInError}>
            {I18n.get(error)}
          </Toast>
        )}
        {!isUserPrivilegesLoading ? (
          render_children
        ) : (
          <span className="spinner d-inline-block">
            <span className="bounce1" />
            <span className="bounce2" />
            <span className="bounce3" />
          </span>
        )}
      </Wrapper>
    );
  }
}
